<template>
  <div class="p-1">
    <ScrollArea class="scroll-area p-2">
      <div class="flex flex-col gap-3 max-h-160">
        <p class="text-black-white text-m-medium px-1 pt-1">
          {{ $t('setting.theme.customTitle') }}
        </p>
        <div class="flex gap-3 flex-wrap p-1">
          <div
            class="group flex flex-col items-center justify-center gap-2 w-47.5 h-30 bg-key rounded-3xl cursor-pointer"
            @click="onUpload"
          >
            <Icon
              icon="Function-Wireframe-Add11"
              class="size-13 text-text-glass-default transition-colors group-hover:text-text-glass-hover group-active:text-brand-blue2"
            />
            <span
              class="text-s-medium text-black-white transition-colors group-active:text-brand-blue2"
            >
              {{ $t('setting.theme.upload') }}
            </span>
          </div>
          <div
            v-for="(wallpaper, index) in userCustomWallpapers"
            :key="wallpaper.id"
            class="cursor-pointer relative overflow-hidden rounded-3xl transition-all hover:ring-2 hover:ring-blue-light-hover"
            :class="[
              currentWallpaper === wallpaper.id &&
                isUserCustom &&
                'ring-2 ring-brand-blue1 hover:ring-brand-blue1',
            ]"
            @click="handleClickItem(wallpaper)"
          >
            <img :src="wallpaper.url" class="w-47.5 h-30 object-cover" />
            <div
              class="text-m-medium text-center absolute left-2.5 top-2.5"
              :class="wallpaper.theme === 'dark' ? 'text-white' : 'text-black'"
            >
              {{ $t('setting.theme.custom') }}{{ index + 1 }}
            </div>
            <div class="absolute right-2.5 top-2.5 flex items-center gap-1">
              <Icon
                icon="Function-Wireframe-Delete"
                class="size-4 transition-colors"
                :class="
                  wallpaper.theme === 'dark'
                    ? 'text-white hover:text-key'
                    : 'text-black hover:text-text-glass-default'
                "
                @click="(e: Event) => handleDeleteWallpaper(wallpaper.id, e)"
              />
              <Switch
                class="data-[state=checked]:bg-black data-[state=unchecked]:bg-white"
                thumb-class="data-[state=checked]:bg-white data-[state=unchecked]:bg-brand-blue1"
                :model-value="wallpaper.theme !== 'dark'"
                @update:model-value="(val) => handleUpdateWallpaper(val, wallpaper.id)"
                @click="(e: Event) => e.stopPropagation()"
              />
            </div>

            <div class="flex gap-3 select-none absolute left-6 top-13 pointer-events-none">
              <Button
                as="div"
                color="glass"
                size="sm"
                class="p-2 h-8"
                :class="wallpaper.theme === 'dark' ? 'text-white' : 'text-black'"
              >
                <Icon icon="Status-Fill-Home"></Icon>
                {{ $t('home.title') }}
              </Button>
              <Button
                as="div"
                color="glass"
                size="sm"
                class="p-2 h-8"
                :class="wallpaper.theme === 'dark' ? 'text-white' : 'text-black'"
              >
                <Icon icon="Status-Fill-Configuration"></Icon>
                {{ $t('home.myConfig2') }}
              </Button>
            </div>

            <div
              class="absolute left-6 top-25 rounded-t-3xl w-full h-8 glass-frosted pointer-events-none"
            />
          </div>
        </div>
        <p class="text-black-white text-m-medium px-1">
          {{ $t('setting.theme.presetTitle') }}
        </p>
        <div class="flex gap-3 flex-wrap p-1">
          <!-- 预设主题 -->
          <div
            v-for="tab in tabs"
            :key="tab.img"
            class="cursor-pointer relative overflow-hidden rounded-3xl transition-all hover:ring-2 hover:ring-blue-light-hover"
            :class="[
              currentWallpaper === tab.img &&
                isPreset &&
                'ring-2 ring-brand-blue1 hover:ring-brand-blue1',
            ]"
            @click="handleClickItem(tab)"
          >
            <img
              :src="getImageUrl(`header/settings/theme/${tab.img}.png`)"
              class="w-47.5 h-30 object-cover"
            />
            <div
              class="text-m-medium text-center absolute left-2.5 top-2.5"
              :class="tab.theme === 'dark' ? 'text-white' : 'text-black'"
            >
              {{ tab.name }}
            </div>

            <div class="flex gap-3 select-none absolute left-6 top-13 pointer-events-none">
              <Button
                as="div"
                color="glass"
                size="sm"
                class="p-2 h-8"
                :class="tab.theme === 'dark' ? 'text-white' : 'text-black'"
              >
                <Icon icon="Status-Fill-Home"></Icon>
                {{ $t('home.title') }}
              </Button>
              <Button
                as="div"
                color="glass"
                size="sm"
                class="p-2 h-8"
                :class="tab.theme === 'dark' ? 'text-white' : 'text-black'"
              >
                <Icon icon="Status-Fill-Configuration"></Icon>
                {{ $t('home.myConfig2') }}
              </Button>
            </div>

            <div
              class="absolute left-6 top-25 rounded-t-3xl w-full h-8 glass-frosted pointer-events-none"
            />
          </div>

          <!-- 自定义主题（组件背景） -->
          <div
            v-for="custom in customTabs"
            :key="custom.id"
            class="cursor-pointer relative overflow-hidden rounded-3xl transition-all hover:ring-2 hover:ring-blue-light-hover"
            :class="[
              currentWallpaper === custom.id &&
                isSystemCustom &&
                'ring-2 ring-brand-blue1 hover:ring-brand-blue1',
            ]"
            @click="handleClickItem(custom)"
          >
            <div class="w-47.5 h-30 relative">
              <component :is="getPreviewComponent(custom.componentName)" class="w-full h-full" />
            </div>
            <div class="text-m-medium text-center absolute left-2.5 top-2.5 text-white">
              {{ custom.name }}
            </div>

            <div class="flex gap-3 select-none absolute left-6 top-13 pointer-events-none">
              <Button as="div" color="glass" size="sm" class="text-white p-2 h-8">
                <Icon icon="Status-Fill-Home"></Icon>
                {{ $t('home.title') }}
              </Button>
              <Button as="div" color="glass" size="sm" class="text-white p-2 h-8">
                <Icon icon="Status-Fill-Configuration"></Icon>
                {{ $t('home.myConfig2') }}
              </Button>
            </div>

            <div
              class="absolute left-6 top-25 rounded-t-3xl w-full h-8 glass-frosted pointer-events-none"
            />
          </div>
        </div>
      </div>
    </ScrollArea>

    <!-- 上传壁纸表单 -->
    <input
      ref="uploader"
      type="file"
      :accept="ACCEPTED_TYPES.join(', ')"
      class="hidden"
      @change="handleUpload"
    />

    <Dialog
      v-model:open="isDeleteDialogOpen"
      :title="$t('setting.theme.deleteTitle')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('setting.theme.deleteConfirm') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" @click="isDeleteDialogOpen = false">{{
            $t('common.cancel')
          }}</Button>
          <Button color="text-b-w" @click="confirmDelete">{{ $t('common.delete') }}</Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
  import { getImageUrl } from '@/utils/assets'
  import { useThemeStore, type ThemeMode } from '@/stores/theme'
  import {
    useWallpaperStore,
    Wallpaper,
    CustomWallpaper,
    CUSTOM_WALLPAPER_CONFIGS,
  } from '@/stores/wallpaper'
  import { storeToRefs } from 'pinia'
  import { Icon, Button, Dialog, ScrollArea, Switch, toast } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { onMounted, ref, useTemplateRef } from 'vue'
  import { isElectron } from '@/utils/env'
  import { BackgroundComponents } from '@/components/backgrounds'
  import { saveWallpaper, getWallpapers, removeWallpaper } from './db'
  import { getImageBrightness } from '@/utils/color'

  interface Tab {
    name: string
    theme: ThemeMode
    img: string
  }

  interface CustomTab {
    name: string
    theme: ThemeMode
    id: string
    /** 对应 BackgroundComponents 注册表中的组件名 */
    componentName: string
  }

  export interface UserCustomWallpaper {
    blob: File
    createdAt: number
    id: string
    mimeType: string
    theme: ThemeMode
    size: number
    order: number
    type: string
  }

  const themeStore = useThemeStore()
  const { t } = useLocaleStore()

  const wallpaperStore = useWallpaperStore()
  const { currentWallpaper, isPreset, isSystemCustom, isUserCustom } = storeToRefs(wallpaperStore)

  const uploader = useTemplateRef<HTMLInputElement>('uploader')
  const userCustomWallpapers = ref<(UserCustomWallpaper & { url: string })[]>([])

  const isDeleteDialogOpen = ref(false)
  const deletingWallpaperId = ref('')

  const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

  const tabs = <Tab[]>[
    { name: t('setting.themes.stealth'), theme: 'dark', img: Wallpaper.STEALTH },
    { name: t('setting.themes.gleam'), theme: 'light', img: Wallpaper.GLEAM },
    { name: t('setting.themes.scroll'), theme: 'light', img: Wallpaper.SCROLL },
    { name: t('setting.themes.pastel'), theme: 'light', img: Wallpaper.PASTEL },
    { name: t('setting.themes.subtle'), theme: 'dark', img: Wallpaper.SUBTLE },
    // { name: t('setting.themes.terminus'), theme: 'dark', img: Wallpaper.TERMINUS },
    { name: t('setting.themes.hackruin'), theme: 'dark', img: Wallpaper.HACKRUIN },
    // { name: t('setting.themes.moonview'), theme: 'dark', img: Wallpaper.MOONVIEW },
    // { name: t('setting.themes.pink'), theme: 'dark', img: Wallpaper.PINK },
    // { name: t('setting.themes.mound'), theme: 'dark', img: Wallpaper.MOUND },
    // { name: t('setting.themes.velvetIsle'), theme: 'light', img: Wallpaper.VELVET_ISLE },
    { name: t('setting.themes.cloudNook'), theme: 'light', img: Wallpaper.CLOUD_NOOK },
    // { name: t('setting.themes.sunny'), theme: 'light', img: Wallpaper.SUNNY },
    { name: t('setting.themes.gentle'), theme: 'light', img: Wallpaper.GENTLE },
  ]

  const customTabs = <CustomTab[]>[
    {
      name: t('setting.themes.refract'),
      theme: 'dark',
      id: CustomWallpaper.REFRACT,
      componentName: CUSTOM_WALLPAPER_CONFIGS[CustomWallpaper.REFRACT].component!,
    },
    {
      name: t('setting.themes.neonBlue'),
      theme: 'dark',
      id: CustomWallpaper.NEON_BLUE,
      componentName: CUSTOM_WALLPAPER_CONFIGS[CustomWallpaper.NEON_BLUE].component!,
    },
  ]

  function resolveTextTheme(
    brightness: number,
    threshold = 145, // 建议 140~150，略偏保守，亮度刚好卡中间时优先判定为"亮"
  ): ThemeMode {
    return brightness < threshold ? 'dark' : 'light'
  }

  /** 根据组件名获取对应的背景组件 */
  const getPreviewComponent = (name: string) => {
    return BackgroundComponents[name] || null
  }

  const handleClickItem = (tab: Tab | CustomTab | (UserCustomWallpaper & { url: string })) => {
    themeStore.setTheme(tab.theme)
    if ('img' in tab) {
      wallpaperStore.selectWallpaper({
        category: 'preset',
        id: tab.img,
      })
      return
    }

    if ('componentName' in tab) {
      const config = CUSTOM_WALLPAPER_CONFIGS[tab.id]
      if (!config) return

      wallpaperStore.selectWallpaper({
        category: 'systemCustom',
        id: tab.id,
        config,
      })
      return
    }

    wallpaperStore.selectWallpaper({
      category: 'userCustom',
      wallpaper: {
        createdAt: tab.createdAt,
        id: tab.id,
        mimeType: tab.mimeType,
        theme: tab.theme,
        size: tab.size,
        order: tab.order,
        type: 'userCustom',
        url: tab.url,
      },
    })
  }

  const onUpload = () => {
    uploader.value?.click()
  }

  const handleUpload = async () => {
    const file = uploader.value?.files?.[0]
    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.warning(t('setting.theme.onlySupportImage', { types: ACCEPTED_TYPES.join(', ') }))
      return
    }

    const url = URL.createObjectURL(file)
    const theme = resolveTextTheme(await getImageBrightness(url))

    const wallpaper: UserCustomWallpaper = {
      blob: file,
      createdAt: Date.now(),
      id: Date.now().toString(),
      mimeType: file.type,
      theme,
      size: file.size,
      order: Date.now(),
      type: 'userCustom',
    }

    await saveWallpaper(wallpaper)
    await loadWallpapers()
  }

  // reka-ui 无法做到T推导类型，故固定使用boolean（true: light | false: dark）
  const handleUpdateWallpaper = async (isLight: boolean, id: string) => {
    const wallpaper = userCustomWallpapers.value.find((item) => item.id === id)
    const theme = isLight ? 'light' : 'dark'

    if (wallpaper) {
      await saveWallpaper({
        ...wallpaper,
        theme,
      })
      await loadWallpapers()

      // 当前壁纸为需要修改系统主题的壁纸时，需要设置全局系统主题
      if (currentWallpaper.value === id) {
        themeStore.setTheme(theme)
      }
    }
  }

  const loadWallpapers = async () => {
    const list = await getWallpapers()

    userCustomWallpapers.value.forEach((item) => {
      URL.revokeObjectURL(item.url)
    })

    userCustomWallpapers.value = list.map((item) => ({
      ...item,
      url: URL.createObjectURL(item.blob),
    }))

    const activeWallpaper = userCustomWallpapers.value.find(
      (item) => item.id === currentWallpaper.value,
    )
    if (isUserCustom.value && activeWallpaper) {
      wallpaperStore.selectWallpaper({
        category: 'userCustom',
        wallpaper: {
          createdAt: activeWallpaper.createdAt,
          id: activeWallpaper.id,
          mimeType: activeWallpaper.mimeType,
          theme: activeWallpaper.theme,
          size: activeWallpaper.size,
          order: activeWallpaper.order,
          type: 'userCustom',
          url: activeWallpaper.url,
        },
      })
    }
  }

  const handleDeleteWallpaper = async (id: string, e: Event) => {
    e.stopPropagation()

    isDeleteDialogOpen.value = true
    deletingWallpaperId.value = id
  }

  const onDeleteWallpaper = async (id: string) => {
    await removeWallpaper(id)
    await loadWallpapers()

    if (id === currentWallpaper.value) {
      const isUserCustom = userCustomWallpapers.value.length > 0

      if (isUserCustom) {
        wallpaperStore.selectWallpaper({
          category: 'userCustom',
          wallpaper: {
            ...userCustomWallpapers.value[0],
            type: 'userCustom',
          },
        })
        return
      }

      wallpaperStore.selectWallpaper({
        category: 'preset',
        id: tabs[0].img,
      })
    }
  }

  const confirmDelete = () => {
    onDeleteWallpaper(deletingWallpaperId.value)

    isDeleteDialogOpen.value = false
    deletingWallpaperId.value = ''
  }

  onMounted(() => {
    loadWallpapers()

    if (!isElectron()) {
      // 预加载所有预设壁纸
      Object.values(Wallpaper).forEach((name) => {
        const img = new Image()
        img.src = getImageUrl(`home/homeBg/${name}.webp`)
      })
    }
  })
</script>

<style>
  .scroll-area {
    div[data-scrollbarimpl] {
      margin-top: 36px !important;
      height: 91%;
    }
  }
</style>
